// The top-level views, in the order they sit in the tab strip.
//
// Overview is first and is where the app lands. Clients held that slot for a while,
// and the argument for it still holds for BROWSING — at forty clients a rail stops
// being navigation, so you search or filter that list and click in
// (planning/crum/client-surface.md §3). What it does not answer is the question you
// actually open the app with, which is what wants you today; a list of every client
// makes you go and find that out. Drilling into a client must not cost the
// cross-cutting views, so every one of them is still reachable flat from here.
//
// Shared by the chrome and by the loading skeleton, so the skeleton draws the SAME strip
// the app is about to draw: the tabs are static, only their counts wait on data. A
// separate list here would drift, which is exactly how the skeleton ended up still
// drawing the left rail a release after the rail was removed.

import type { IconName } from '../components/Icon'
import { accentVar, type EntityKind } from './model'

export interface Tab {
  to: string
  label: string
  icon: IconName
  /** the record kind this view lists, if any: the tab wears that kind's colour */
  kind?: EntityKind
  /**
   * Which part of the strip it belongs to. Five tabs name a record kind and are
   * how you browse; four are surfaces you go to on purpose; one is where you land.
   * They were nine identical items, which is what made the app read as nine equal
   * rooms rather than a filing cabinet plus a workbench. A rule and a quieter
   * weight is the whole fix — no route moves.
   */
  group: 'home' | 'record' | 'surface'
}

export const TABS: Tab[] = [
  // Where the app lands. Not a list of a kind and not a surface you go to on
  // purpose — you arrive here, so it reads at a record tab's weight rather than a
  // surface's.
  { to: '/overview', label: 'Overview', icon: 'LayoutDashboard', group: 'home' },
  // "Clients" is a label over orgs-with-a-deal, not a record type: an org's
  // relationship moves (prospect → client → past), and freezing that into the type
  // is what crum already removed once. See client-surface.md §3a.
  { to: '/orgs', label: 'Clients', icon: 'Building2', kind: 'org', group: 'record' },
  { to: '/deals', label: 'Deals', icon: 'Handshake', kind: 'deal', group: 'record' },
  // Tabs name TYPES. A project is an ongoing effort; an artifact is a thing a
  // project produced. They were briefly one record shown two ways, which meant the
  // tab bar named something the model did not have.
  { to: '/work', label: 'Projects', icon: 'Boxes', kind: 'project', group: 'record' },
  { to: '/artifacts', label: 'Artifacts', icon: 'Layers', kind: 'artifact', group: 'record' },
  { to: '/people', label: 'People', icon: 'User', kind: 'person', group: 'record' },
  { to: '/due', label: 'Due', icon: 'Check', group: 'surface' },
  // Agents propose here; a person approves and the log records the person.
  { to: '/staging', label: 'Staging', icon: 'Archive', group: 'surface' },
  { to: '/network', label: 'Network', icon: 'Network', group: 'surface' },
  { to: '/activity', label: 'Activity', icon: 'Activity', group: 'surface' },
]

/** Views that list a kind wear its colour; the ones that span kinds stay neutral,
 *  except the landing, which wears the product's own accent. */
export function hueVar(tab: Tab): string {
  if (tab.kind) return `var(${accentVar(tab.kind)})`
  return tab.group === 'home' ? 'var(--accent)' : 'var(--muted)'
}

/**
 * Which tab is lit for a route the router does not match on its own.
 *
 * This used to run only for `/e/:id`, which meant the two screens you are on most
 * — a client at `/c/:orgId` and a board at `/b/:workId` — lit NOTHING. The strip
 * going dark exactly where you drill in is the literal cause of not knowing where
 * you are, so every route that is not a top-level view names its place here.
 *
 * `entityKind` is passed in rather than the whole store so this stays a pure
 * function of the path plus one lookup.
 */
export function placeOf(
  pathname: string,
  entityKind: (id: string) => EntityKind | undefined,
): string | undefined {
  if (pathname.startsWith('/e/')) {
    const kind = entityKind(pathname.slice(3).split('?')[0] ?? '')
    return kind ? KIND_TAB[kind] : undefined
  }
  // A client page and a board are both reached from Clients and both belong to it:
  // a board is one project's surface, and a project belongs to a client.
  if (pathname.startsWith('/c/')) return '/orgs'
  if (pathname.startsWith('/b/')) return '/work'
  return undefined
}

/** Which tab a route belongs to, including the detail pages: a deal you opened from
 *  anywhere still reads as part of Deals, so the header doesn't go blank. */
export const KIND_TAB: Record<string, string> = {
  deal: '/deals',
  project: '/work',
  artifact: '/artifacts',
  org: '/orgs',
  person: '/people',
}

/** The strip and one tab in it, shared so the skeleton cannot drift from the real thing:
 *  the mismatch this caught was a 20px-wide icon the skeleton drew and the live tab hides
 *  from `sm:` up, which shifted every tab after Overview. */
export const STRIP_CLASS = 'flex gap-0.5 overflow-x-auto px-2 sm:px-3'
export const TAB_CLASS =
  'inline-flex shrink-0 items-center gap-1.5 px-2.5 pb-1.5 pt-1.5 text-body sm:px-3'

/** The accent appears on the underline and the icon, never on the label.
 *  A surface tab sits one step quieter when it is not the active one, which is what
 *  separates "browse these" from "go here on purpose" without moving any route. */
export function tabStyle(tab: Tab, on: boolean): React.CSSProperties {
  const off = tab.group === 'surface' ? 'var(--faint)' : 'var(--muted)'
  return {
    color: on ? 'var(--fg)' : off,
    fontWeight: on ? 570 : 450,
    borderBottom: `2px solid ${on ? hueVar(tab) : 'transparent'}`,
  }
}

/** The tab a raw hash route sits under, for code that runs before the router mounts. */
export function tabForHash(hash: string): Tab | undefined {
  const path = hash.replace(/^#/, '').split('?')[0] ?? ''
  return TABS.find((t) => path === t.to || path.startsWith(`${t.to}/`))
}
